/**
 * VideoLibraryTab - Video management with add/edit modals
 * Extracted from TrainingView.jsx
 */

import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Video, Plus, Trash2, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoLibraryTab({ videos, setVideos }) {
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [showEditVideo, setShowEditVideo] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({ title: '', url: '', duration: '', description: '' });

  const addVideo = async () => {
    try {
      await axios.post(`${API}/training/videos`, videoForm, { withCredentials: true });
      toast.success('Video added!');
      setShowAddVideo(false);
      setVideoForm({ title: '', url: '', duration: '', description: '' });
      const res = await axios.get(`${API}/training/videos`, { withCredentials: true });
      setVideos(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add video');
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/training/videos/${videoId}`, { withCredentials: true });
      toast.success('Video deleted!');
      const safeVideos = Array.isArray(videos) ? videos : [];
      setVideos(safeVideos.filter(v => v.video_id !== videoId));
    } catch (e) {
      toast.error('Failed to delete video');
    }
  };

  const openEditVideo = (video) => {
    setEditingVideo(video);
    setVideoForm({
      title: video.title || '',
      url: video.url || '',
      duration: video.duration || '',
      description: video.description || ''
    });
    setShowEditVideo(true);
  };

  const updateVideo = async () => {
    if (!editingVideo) return;
    try {
      await axios.put(`${API}/training/videos/${editingVideo.video_id}`, videoForm, { withCredentials: true });
      toast.success('Video updated!');
      setShowEditVideo(false);
      setEditingVideo(null);
      setVideoForm({ title: '', url: '', duration: '', description: '' });
      const res = await axios.get(`${API}/training/videos`, { withCredentials: true });
      setVideos(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update video');
    }
  };

  return (
    <>
      <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border/30 overflow-hidden">
        <div className="p-4 border-b border-border/30 flex items-center justify-between">
          <h3 className="text-lg font-bold">Video Library</h3>
          <button onClick={() => setShowAddVideo(true)} className="px-4 py-2 bg-accent text-black rounded-xl font-medium flex items-center gap-2" data-testid="add-video-btn">
            <Plus className="w-4 h-4" /> Add Video
          </button>
        </div>
        <div className="divide-y divide-border/30">
          {(!Array.isArray(videos) || videos.length === 0) ? (
            <div className="p-8 text-center text-muted-foreground">
              No videos yet. Click "Add Video" to add your first training video.
            </div>
          ) : (
            videos.map((v) => (
              <div key={v.video_id} className="p-4 flex items-center gap-4 hover:bg-accent/5 transition-colors">
                <Video className="w-8 h-8 text-red-400" />
                <div className="flex-1">
                  <p className="font-medium">{v.title}</p>
                  <p className={`text-sm ${!v.duration_seconds ? 'text-orange-400' : 'text-muted-foreground'}`}>
                    {v.duration_seconds ? `${v.duration} (${v.duration_seconds}s)` : '⚠️ Duration not set - click Edit'} • {v.description || 'No description'}
                  </p>
                </div>
                <button onClick={() => openEditVideo(v)} className="p-2 hover:bg-blue-500/20 rounded-lg text-blue-400" data-testid={`edit-video-${v.video_id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => deleteVideo(v.video_id)} className="p-2 hover:bg-red-500/20 rounded-lg text-red-400" data-testid={`delete-video-${v.video_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Video Modal */}
      {showAddVideo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => { setShowAddVideo(false); setVideoForm({ title: '', url: '', duration: '', description: '' }); }}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Add Training Video</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Video Title *</label>
                <input
                  type="text"
                  value={videoForm.title}
                  onChange={e => setVideoForm({...videoForm, title: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="Safety Training Video 1"
                  data-testid="video-title-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Video URL *</label>
                <input
                  type="url"
                  value={videoForm.url}
                  onChange={e => setVideoForm({...videoForm, url: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="video-url-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Duration (required for accurate progress tracking)</label>
                <input
                  type="text"
                  value={videoForm.duration}
                  onChange={e => setVideoForm({...videoForm, duration: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="0:51 or 3:20 (mm:ss format)"
                  data-testid="video-duration-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Enter video length in mm:ss format (e.g., 0:51 for 51 seconds, 3:20 for 3 min 20 sec)</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Description</label>
                <textarea
                  value={videoForm.description}
                  onChange={e => setVideoForm({...videoForm, description: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  rows={2}
                  placeholder="What this training covers..."
                  data-testid="video-description-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAddVideo(false)} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button onClick={addVideo} disabled={!videoForm.title || !videoForm.url} className="flex-1 py-3 bg-accent text-black rounded-xl font-bold disabled:opacity-50" data-testid="save-video-btn">Add Video</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Video Modal */}
      {showEditVideo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => { setShowEditVideo(false); setEditingVideo(null); }}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-400" />
              Edit Training Video
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Video Title *</label>
                <input
                  type="text"
                  value={videoForm.title}
                  onChange={e => setVideoForm({...videoForm, title: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="Safety Training Video 1"
                  data-testid="edit-video-title-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Video URL *</label>
                <input
                  type="url"
                  value={videoForm.url}
                  onChange={e => setVideoForm({...videoForm, url: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="edit-video-url-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground font-bold text-orange-400">Duration (REQUIRED for progress tracking) *</label>
                <input
                  type="text"
                  value={videoForm.duration}
                  onChange={e => setVideoForm({...videoForm, duration: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border-2 border-orange-500 focus:border-accent outline-none"
                  placeholder="0:51 or 3:20 (mm:ss format)"
                  data-testid="edit-video-duration-input"
                />
                <p className="text-xs text-orange-400 mt-1">⚠️ Enter video length in mm:ss format (e.g., 0:51 for 51 seconds, 3:20 for 3 min 20 sec)</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Description</label>
                <textarea
                  value={videoForm.description}
                  onChange={e => setVideoForm({...videoForm, description: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  rows={2}
                  placeholder="What this training covers..."
                  data-testid="edit-video-description-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowEditVideo(false); setEditingVideo(null); }} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button onClick={updateVideo} disabled={!videoForm.title || !videoForm.url || !videoForm.duration} className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold disabled:opacity-50" data-testid="update-video-btn">Update Video</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
